import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ClientService } from './client.service';

export enum AnnotationTargetType {
  None = 0,
  Deck = 1,
  Node = 2,
}

export interface AnnotationModel {
  id?: number;
  publicId?: string;
  workspaceId?: number;
  text?: string;
  source?: string | null;
  deletedAt?: string | null;
}

export type AnnotationUpsertPayload = {
  id?: number;
  workspaceId: number;
  text: string;
  source?: string | null;
  deletedAt?: string | null;
};

type AnnotationResponse =
  | AnnotationModel
  | {
      data?: AnnotationModel;
      item?: AnnotationModel;
      value?: AnnotationModel;
    };

type AnnotationsResponse =
  | AnnotationModel[]
  | {
      data?: AnnotationModel[];
      items?: AnnotationModel[];
      value?: AnnotationModel[];
    };

@Injectable({ providedIn: 'root' })
export class AnnotationService {
  private readonly client = inject(ClientService);

  get(id: number): Observable<AnnotationModel> {
    return this.client
      .get<AnnotationResponse>(`annotation/${id}`)
      .pipe(map((response) => this.unwrapItem(response)));
  }

  list(): Observable<AnnotationModel[]> {
    return this.client
      .get<AnnotationsResponse>('annotation')
      .pipe(map((response) => this.unwrapList(response)));
  }

  listByTarget(
    targetType: AnnotationTargetType,
    targetId: string,
  ): Observable<AnnotationModel[]> {
    return this.client
      .get<AnnotationsResponse>(
        `annotation/target/${targetType}/${encodeURIComponent(targetId)}`,
      )
      .pipe(map((response) => this.unwrapList(response)));
  }

  create(payload: AnnotationUpsertPayload): Observable<AnnotationModel> {
    return this.client
      .post<AnnotationResponse, AnnotationUpsertPayload>('annotation', payload)
      .pipe(map((response) => this.unwrapItem(response)));
  }

  update(payload: AnnotationUpsertPayload): Observable<AnnotationModel> {
    return this.client
      .put<AnnotationResponse, AnnotationUpsertPayload>('annotation', payload)
      .pipe(map((response) => this.unwrapItem(response)));
  }

  delete(id: number): Observable<boolean> {
    return this.client.delete<unknown>(`annotation/${id}`).pipe(
      map((response) => {
        if (typeof response === 'boolean') {
          return response;
        }

        if (typeof (response as { data?: unknown })?.data === 'boolean') {
          return Boolean((response as { data?: unknown }).data);
        }

        if (typeof (response as { value?: unknown })?.value === 'boolean') {
          return Boolean((response as { value?: unknown }).value);
        }

        return true;
      }),
    );
  }

  private unwrapItem(response: AnnotationResponse): AnnotationModel {
    const wrapped = response as Record<string, unknown>;
    const data = wrapped['data'] ?? wrapped['Data'];
    const item = wrapped['item'] ?? wrapped['Item'];
    const value = wrapped['value'] ?? wrapped['Value'];

    if (this.isModel(data)) {
      return this.normalize(data);
    }

    if (this.isModel(item)) {
      return this.normalize(item);
    }

    if (this.isModel(value)) {
      return this.normalize(value);
    }

    if (this.isModel(response)) {
      return this.normalize(response);
    }

    return {};
  }

  private unwrapList(response: AnnotationsResponse): AnnotationModel[] {
    if (Array.isArray(response)) {
      return response.map((item) => this.normalize(item));
    }

    const wrapped = response as Record<string, unknown>;
    const data = wrapped['data'] ?? wrapped['Data'];
    const items = wrapped['items'] ?? wrapped['Items'];
    const value = wrapped['value'] ?? wrapped['Value'];

    if (Array.isArray(data)) {
      return data.map((item) => this.normalize(item as AnnotationModel));
    }

    if (Array.isArray(items)) {
      return items.map((item) => this.normalize(item as AnnotationModel));
    }

    if (Array.isArray(value)) {
      return value.map((item) => this.normalize(item as AnnotationModel));
    }

    return [];
  }

  private isModel(value: unknown): value is AnnotationModel {
    if (typeof value !== 'object' || value === null) {
      return false;
    }

    const record = value as Record<string, unknown>;
    return (
      'id' in record ||
      'Id' in record ||
      'publicId' in record ||
      'PublicId' in record
    );
  }

  private normalize(model: AnnotationModel): AnnotationModel {
    const record = model as Record<string, unknown>;

    return {
      id: this.toOptionalNumber(record['id'] ?? record['Id']),
      publicId: this.toOptionalString(record['publicId'] ?? record['PublicId']),
      workspaceId: this.toOptionalNumber(
        record['workspaceId'] ?? record['WorkspaceId'],
      ),
      text: this.toOptionalString(record['text'] ?? record['Text']),
      source: this.toNullableString(record['source'] ?? record['Source']),
      deletedAt: this.toNullableString(
        record['deletedAt'] ?? record['DeletedAt'],
      ),
    };
  }

  private toOptionalNumber(value: unknown): number | undefined {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  private toOptionalString(value: unknown): string | undefined {
    return typeof value === 'string' ? value : undefined;
  }

  private toNullableString(value: unknown): string | null | undefined {
    if (value == null) {
      return value as null | undefined;
    }

    return typeof value === 'string' ? value : String(value);
  }
}
